import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/app/AppShell";
import { api } from "../lib/api";
import { useT } from "../contexts/LanguageContext";
import { toast } from "sonner";

const DOMAINS = ["all", "People", "Process", "Business Environment"];
const DIFFS = ["all", "easy", "medium", "hard"];

export default function QuizPractice() {
  const { t, tDomain, tDiff, lang } = useT();
  const navigate = useNavigate();
  const [domain, setDomain] = useState("all");
  const [difficulty, setDifficulty] = useState("all");
  const [count, setCount] = useState(10);
  const [adaptive, setAdaptive] = useState(false);
  const [starting, setStarting] = useState(false);

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/quiz/start", { mode: "practice", domain, difficulty, count: Number(count), lang, adaptive });
      navigate("/quiz/run", { state: { quiz: data, mode: "practice" } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("practice.could_not_start"));
    } finally {
      setStarting(false);
    }
  };

  return (
    <AppShell>
      <div className="overline text-[#002FA7]">{t("practice.overline")}</div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">{t("practice.title")}</h1>
      <p className="text-slate-600 mt-2 max-w-xl">{t("practice.subtitle")}</p>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl">
        <div>
          <label className="overline">{t("practice.domain")}</label>
          <select value={domain} onChange={(e) => setDomain(e.target.value)} data-testid="practice-domain-select">
            {DOMAINS.map((d) => <option key={d} value={d}>{d === "all" ? t("common.all") : tDomain(d)}</option>)}
          </select>
        </div>
        <div>
          <label className="overline">{t("practice.difficulty")}</label>
          <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} disabled={adaptive} data-testid="practice-difficulty-select">
            {DIFFS.map((d) => <option key={d} value={d}>{d === "all" ? t("common.any") : tDiff(d)}</option>)}
          </select>
        </div>
        <div>
          <label className="overline">{t("practice.questions")}</label>
          <select value={count} onChange={(e) => setCount(e.target.value)} data-testid="practice-count-select">
            {[5, 10, 15, 25, 40].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-6 max-w-3xl">
        <label className="flex items-start gap-3 cursor-pointer p-4 rounded-md border border-slate-200 hover:border-[#002FA7] transition-colors" data-testid="practice-adaptive-toggle">
          <input
            type="checkbox"
            checked={adaptive}
            onChange={(e) => setAdaptive(e.target.checked)}
            className="!w-4 !h-4 mt-0.5"
            data-testid="practice-adaptive-checkbox"
          />
          <span>
            <span className="font-semibold text-sm flex items-center gap-1">
              <span className="text-[#002FA7]">✦</span> {t("practice.adaptive")}
            </span>
            <span className="block text-xs text-slate-500 mt-1">{t("practice.adaptive_hint")}</span>
          </span>
        </label>
      </div>

      <div className="mt-8">
        <button onClick={start} disabled={starting} className="btn-primary" data-testid="practice-start-button">
          {starting ? t("practice.preparing") : t("practice.start")}
        </button>
      </div>
    </AppShell>
  );
}
